import React, { FC } from "react";
import { AssetLocation, AssetLocationTypes, MovementRuleAction } from "../../../logic/api";
import { ISessionState } from "../../../utility/useSession";
import { KeyLocationFields } from "./locationFields/KeyLocationFields";
import { LaneLocationFields } from "./locationFields/LaneLocationFields";
import { ReleaseFlag, useReleaseManagement } from "../../../utility/feature-management";

export type KRT = keyof typeof MovementRuleAction;

export const getAcceptableLocationTypes = (
  keyLocationCreationEnabled: boolean,
  parentLocation: AssetLocation | null
) => {
  const acceptableChildLocations = new Map<number, AssetLocationTypes[]>([
    [
      -1, // This will be matched if the asset location has no parent
      [AssetLocationTypes.CarPark]
    ],
    [
      AssetLocationTypes.CarPark,
      [AssetLocationTypes.Zone, AssetLocationTypes.KeyLocker]
    ],
    [
      AssetLocationTypes.Zone,
      [AssetLocationTypes.Lane]
    ],
    [
      AssetLocationTypes.KeyLocker,
      [AssetLocationTypes.KeyLockerBox]
    ],
  ]);

  if (keyLocationCreationEnabled) {
    acceptableChildLocations.set(
      AssetLocationTypes.CarPark,
      [AssetLocationTypes.Zone, AssetLocationTypes.KeyLocker, AssetLocationTypes.Keyroom]
    );

    acceptableChildLocations.set(
      AssetLocationTypes.Keyroom,
      [AssetLocationTypes.Hook, AssetLocationTypes.KeyBox, AssetLocationTypes.DynamicBox]
    );
  }

  const parentLocationType = parentLocation ? parentLocation.type : -1;
  return acceptableChildLocations.get(parentLocationType) ?? [];
}

export const isKeyLocation = (locationType: AssetLocationTypes) => {
  return [
    AssetLocationTypes.KeyBox,
    AssetLocationTypes.DynamicBox,
    AssetLocationTypes.Hook,
    AssetLocationTypes.KeyLockerBox
  ].includes(locationType);
}

export interface LocationFieldsProps {
  session: ISessionState;
  location: AssetLocation;
  parentLocation: AssetLocation | null;
  onGpsLocationValidityChange?: (isValid: boolean) => void;
}

export const LocationFields: FC<LocationFieldsProps> = ({ session, location, parentLocation, onGpsLocationValidityChange }) => {
  const dateKeyBoxEnabled = useReleaseManagement(ReleaseFlag.releaseDateKeyBox);

  if (dateKeyBoxEnabled && isKeyLocation(location.type)) {
    return <KeyLocationFields location={location} parentLocation={parentLocation} session={session} />
  }

  return <LaneLocationFields location={location} parentLocation={parentLocation} session={session} onGpsLocationValidityChange={onGpsLocationValidityChange} />
}
